/* eslint-disable react-refresh/only-export-components */
import { lazy, Suspense } from "react";
import {
  createBrowserRouter,
  Navigate,
  useRouteError,
  useSearchParams,
} from "react-router-dom";

import {
  AppLayout,
  DataLoader,
  Fixture,
  ScheduleCarousel,
} from "./components";

const BubbleError = () => {
  const error = useRouteError();
  throw error;
};

function LazyRoute({ componentName, componentProps }: {
  componentName: string;
  componentProps?: Record<string, unknown>;
}) {
  const LazyElement = lazy(
    () => import(`./components/${componentName}/${componentName}.tsx`),
  );
  return (
    <Suspense fallback={<DataLoader customStyle={{ height: "90dvh" }} />}>
      <LazyElement {...(componentProps ?? {})} />
    </Suspense>
  );
}

// On `/`, swap between the box-score (`Fixture`) and tournament stats based
// on `?view=tournament-stats`. The desktop CTA inside FixtureBoxScore sets
// this param so the stats panel opens below the carousel without navigation.
function IndexRoute() {
  const [searchParams] = useSearchParams();
  if (searchParams.get("view") === "tournament-stats") {
    return <LazyRoute componentName="TournamentStatsPage" />;
  }
  return <Fixture />;
}

type LazyRouteDef = {
  path: string;
  component: string;
  standalone?: boolean;
  props?: Record<string, unknown>;
};
const LAZY_ROUTES: LazyRouteDef[] = [
  { path: "standings", component: "MatchStandings", standalone: true },
  {
    path: "fixtures-list",
    component: "FixturesList",
    standalone: true,
    props: { accordion: true },
  },
  { path: "tournament-stats", component: "TournamentStatsPage" },
  { path: "knockout-and-groups", component: "KnockoutBracket" },
];

export const router = createBrowserRouter([
  // Debug page mounted OUTSIDE AppLayout so it doesn't trigger profile/init/
  // match-data fetches when an admin opens it.
  {
    path: "/cache-metrics",
    element: <LazyRoute componentName="CacheMetrics" />,
    errorElement: <BubbleError />,
  },
  {
    path: "/",
    element: <AppLayout />,
    errorElement: <BubbleError />,
    children: [
      { index: true, element: <IndexRoute /> },
      {
        path: "carousel",
        element: (
          <ScheduleCarousel
            onSelect={(m) =>
              window.parent.postMessage(
                {
                  type: "SBS_MATCH_SELECTED",
                  matchId: m.id,
                  source: "schedule-carousel",
                },
                "*",
              )
            }
          />
        ),
      },
      { path: "match-centre", element: <Fixture /> },
      ...LAZY_ROUTES.map(({ path, component, standalone, props }) => {
        const el = (
          <LazyRoute
            key={path}
            componentName={component}
            componentProps={props}
          />
        );
        return {
          path,
          element: standalone ? (
            <div className="standalone-page">{el}</div>
          ) : (
            el
          ),
        };
      }),
      { path: "*", element: <Navigate to="/" replace /> },
    ],
  },
]);
